import { type ComputedRef, type Ref, computed, ref } from 'vue';

import { PlacementType } from '../types';

export type AlignOverflow = {
  adjustX?: boolean | number;
  adjustY?: boolean | number;
  alwaysByViewport?: boolean;
  shiftX?: boolean | number;
  shiftY?: boolean | number;
};

type AlignWithOverflow = { overflow?: AlignOverflow };

export function withViewportOverflow<T extends AlignWithOverflow>(
  align: T | undefined,
): T & { overflow: AlignOverflow } {
  return {
    ...(align ?? ({} as T)),
    overflow: {
      adjustX: true,
      adjustY: true,
      alwaysByViewport: true,
      ...align?.overflow,
    },
  };
}

export function useViewportPlacement(
  placement: Ref<PlacementType>,
  mountToBody: Ref<boolean>,
  getPopupDomNode: () => HTMLElement | null,
): {
  effectivePlacement: ComputedRef<PlacementType>;
  getPopupContainer: (triggerNode: HTMLElement) => HTMLElement;
  onShow: () => void;
  onHide: () => void;
} {
  const triggerEl = ref<HTMLElement | null>(null);
  const flippedPlacement = ref<PlacementType | null>(null);

  function getPopupContainer(triggerNode: HTMLElement): HTMLElement {
    triggerEl.value = triggerNode;
    if (mountToBody.value) {
      return (triggerNode?.closest('dialog') ?? document.body) as HTMLElement;
    }
    return triggerNode?.parentElement as HTMLElement;
  }

  // ant-design fires open-change before the popup is appended to the DOM
  // and rendered with its real height, so we poll a few animation frames
  // until the popup is available, then flip placement only when the popup
  // would actually overflow on the requested side AND the opposite side has
  // more room. Flipping based on available space alone would surprise
  // consumers (e.g. a top-placed tooltip near the upper viewport edge).
  function measureAndMaybeFlip(framesLeft: number) {
    const trigger = triggerEl.value;
    const popup = getPopupDomNode();
    const popupHeight = popup?.getBoundingClientRect().height ?? 0;
    if ((!popup || popupHeight === 0) && framesLeft > 0) {
      requestAnimationFrame(() => measureAndMaybeFlip(framesLeft - 1));
      return;
    }
    if (!trigger || !popup) return;
    const { top, bottom } = trigger.getBoundingClientRect();
    const spaceAbove = top;
    const spaceBelow = window.innerHeight - bottom;
    const requested = placement.value;
    if (
      requested.startsWith('bottom') &&
      popupHeight > spaceBelow &&
      spaceAbove > spaceBelow
    ) {
      flippedPlacement.value = requested.replace(
        'bottom',
        'top',
      ) as PlacementType;
    } else if (
      requested.startsWith('top') &&
      popupHeight > spaceAbove &&
      spaceBelow > spaceAbove
    ) {
      flippedPlacement.value = requested.replace(
        'top',
        'bottom',
      ) as PlacementType;
    }
  }

  function onShow() {
    if (!mountToBody.value) return;
    measureAndMaybeFlip(10);
  }

  function onHide() {
    flippedPlacement.value = null;
  }

  return {
    effectivePlacement: computed(
      () => flippedPlacement.value ?? placement.value,
    ),
    getPopupContainer,
    onShow,
    onHide,
  };
}
